//src/pages/PermissoesUsuario.js
import React, { useEffect, useMemo, useState } from "react";
import axios from "../axiosConfig";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CheckCircle2,
  Save,
  ShieldCheck,
  SlidersHorizontal,
} from "lucide-react";

import "./PermissoesUsuario.css";

const todasAsPermissoes = [
  { rota: "usuarios", nome: "Usuários" },
  { rota: "clientes", nome: "Clientes" },
  { rota: "perfis-cliente", nome: "Perfis de Cliente" },
  { rota: "empresas", nome: "Empresas" },
  { rota: "operacoes", nome: "Operações" },
  { rota: "resgates", nome: "Resgates" },
  { rota: "premios", nome: "Prêmios" },
  { rota: "historico-pontos", nome: "Histórico de Pontos" },
  { rota: "relatorio-pontos", nome: "Relatório de Pontos" },
  { rota: "tikpag-bi", nome: "BI Tikpag" },
  { rota: "credito-manual", nome: "Creditar Pontos" },
  { rota: "configuracoes-valor-ponto", nome: "Valor do Ponto" },
  { rota: "configuracoes-frete", nome: "Configurações de Frete" },
  { rota: "expedicao", nome: "Expedição" },
  { rota: "dashboard", nome: "Dashboard" },
  { rota: "campanhas", nome: "Campanhas" },
  { rota: "pix", nome: "Pix Premiado" },
  { rota: "tikpag_experience", nome: "Tikpag Experience" },
];

const campos = [
  { key: "pode_ler", label: "Ler" },
  { key: "pode_inserir", label: "Inserir" },
  { key: "pode_editar", label: "Editar" },
  { key: "pode_excluir", label: "Excluir" },
  { key: "pode_aprovar", label: "Aprovar" },
  { key: "pode_exportar", label: "Exportar" },
];

export default function PermissoesUsuario() {
  const { id } = useParams();
  const navigate = useNavigate();

  const [permissoes, setPermissoes] = useState([]);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(true);

  useEffect(() => {
    const carregarPermissoes = async () => {
      try {
        setCarregando(true);

        const res = await axios.get(`/usuarios/${id}/permissoes`);
        const dados = Array.isArray(res.data) ? res.data : [];

        setPermissoes(
          todasAsPermissoes.map((p) => {
            const existente = dados.find((d) => d.rota === p.rota);

            return {
              rota: p.rota,
              pode_ler: Boolean(existente?.pode_ler),
              pode_inserir: Boolean(existente?.pode_inserir),
              pode_editar: Boolean(existente?.pode_editar),
              pode_excluir: Boolean(existente?.pode_excluir),
              pode_aprovar: Boolean(existente?.pode_aprovar),
              pode_exportar: Boolean(existente?.pode_exportar),
            };
          })
        );
      } catch (err) {
        console.error("Erro ao buscar permissões:", err);
      } finally {
        setCarregando(false);
      }
    };

    carregarPermissoes();
  }, [id]);

  const toggle = (rota, campo) => {
    setPermissoes((prev) =>
      prev.map((p) =>
        p.rota === rota ? { ...p, [campo]: !p[campo] } : p
      )
    );
  };

  const salvar = async () => {
    setSalvando(true);

    try {
      await axios.post(`/usuarios/${id}/permissoes`, { permissoes });
      alert("Permissões salvas com sucesso!");
      navigate(-1);
    } catch (err) {
      console.error("Erro ao salvar permissões:", err);
      alert("Erro ao salvar permissões.");
    } finally {
      setSalvando(false);
    }
  };

  const resumo = useMemo(() => {
    const totalAreas = permissoes.length;
    const areasComAcesso = permissoes.filter((p) =>
      campos.some((c) => p[c.key])
    ).length;

    const totalMarcadas = permissoes.reduce((acc, p) => {
      return acc + campos.filter((c) => p[c.key]).length;
    }, 0);

    return {
      totalAreas,
      areasComAcesso,
      totalMarcadas,
    };
  }, [permissoes]);

  return (
    <div className="permissoesUsuarioAdmin-page">
      <div className="permissoesUsuarioAdmin-topbar">
        <div>
          <span className="permissoesUsuarioAdmin-kicker">
            Controle de acesso
          </span>

          <h2>Permissões do usuário #{id}</h2>

          <p>
            Defina quais áreas do painel este usuário pode visualizar, editar,
            aprovar, exportar ou gerenciar.
          </p>
        </div>

        <button
          type="button"
          className="permissoesUsuarioAdmin-btn secondary"
          onClick={() => navigate(-1)}
        >
          <ArrowLeft size={16} />
          Voltar
        </button>
      </div>

      <div className="permissoesUsuarioAdmin-cards">
        <div className="permissoesUsuarioAdmin-card">
          <span>Áreas disponíveis</span>
          <strong>{resumo.totalAreas.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="permissoesUsuarioAdmin-card active">
          <span>Áreas com acesso</span>
          <strong>{resumo.areasComAcesso.toLocaleString("pt-BR")}</strong>
        </div>

        <div className="permissoesUsuarioAdmin-card permissions">
          <span>Permissões marcadas</span>
          <strong>{resumo.totalMarcadas.toLocaleString("pt-BR")}</strong>
        </div>
      </div>

      <div className="permissoesUsuarioAdmin-tableCard">
        <div className="permissoesUsuarioAdmin-tableHeader">
          <div>
            <h3>Matriz de permissões</h3>
            <span>Marque os acessos permitidos para cada área do sistema.</span>
          </div>

          <SlidersHorizontal size={18} />
        </div>

        <div className="permissoesUsuarioAdmin-tableWrapper">
          <table className="permissoesUsuarioAdmin-table">
            <thead>
              <tr>
                <th>Área</th>
                {campos.map((c) => (
                  <th key={c.key}>{c.label}</th>
                ))}
              </tr>
            </thead>

            <tbody>
              {carregando ? (
                <tr>
                  <td
                    colSpan={campos.length + 1}
                    className="permissoesUsuarioAdmin-empty"
                  >
                    Carregando permissões...
                  </td>
                </tr>
              ) : (
                permissoes.map((perm) => {
                  const area = todasAsPermissoes.find(
                    (p) => p.rota === perm.rota
                  );

                  const possuiAlgumAcesso = campos.some((c) => perm[c.key]);

                  return (
                    <tr key={perm.rota}>
                      <td>
                        <div className="permissoesUsuarioAdmin-areaCell">
                          <div
                            className={`permissoesUsuarioAdmin-areaIcon ${
                              possuiAlgumAcesso ? "ativo" : ""
                            }`}
                          >
                            <ShieldCheck size={15} />
                          </div>

                          <div>
                            <strong>{area?.nome || perm.rota}</strong>
                            <span>{perm.rota}</span>
                          </div>
                        </div>
                      </td>

                      {campos.map((c) => (
                        <td key={c.key}>
                          <label className="permissoesUsuarioAdmin-toggle">
                            <input
                              type="checkbox"
                              checked={Boolean(perm[c.key])}
                              onChange={() => toggle(perm.rota, c.key)}
                            />
                            <span />
                          </label>
                        </td>
                      ))}
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>

        <div className="permissoesUsuarioAdmin-actions">
          <button
            type="button"
            className="permissoesUsuarioAdmin-btn secondary"
            onClick={() => navigate(-1)}
          >
            <ArrowLeft size={16} />
            Voltar
          </button>

          <button
            type="button"
            className="permissoesUsuarioAdmin-btn primary"
            onClick={salvar}
            disabled={salvando}
          >
            {salvando ? (
              <>
                <CheckCircle2 size={16} />
                Salvando...
              </>
            ) : (
              <>
                <Save size={16} />
                Salvar permissões
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}